import { patchFilter, withHistoryUpdate } from "./functions";
/**
 * Performs all required minimal work to update a given document, and persist it.
 *
 * @param previousDocument - document we are updating
 * @param mutationFn - function to mutate the document
 * @param actionName - name of action associated with this update, this should be either a string or a string generator. We pass a string generator when using this in autopilot to determine the proper text shown in action record and make sure that matches the one we show in messages
 * @param dependencies - needed dependencies
 * @param actionUserUid - user uid for the action applied to the document. When it's undefined, we fallback to the owner_uid of the document
 *
 * @returns the updated document
 */
export async function updateAndPersistDocumentData(previousDocument, mutationFn, actionName, dependencies, actionUserUid) {
    const { actionRecordRepository, documentRepository, logger } = dependencies;
    const newDocument = withHistoryUpdate(previousDocument, mutationFn);
    const latestPatch = newDocument.latestPatch;
    // If the mutation was a no-op (no actual changes), return the document unchanged.
    // This can happen when the action doesn't modify any document data (e.g., trying
    // to add a component that already exists with the same configuration).
    if (!latestPatch) {
        return previousDocument;
    }
    const now = new Date().getTime();
    // Update action record
    const updateActionRecordPromise = actionRecordRepository
        .save(newDocument.uid, {
        actionChangeDiff: {
            uid: latestPatch.uid,
            type: latestPatch.type,
            // Filter out patches with paths containing "selectedObjectUids" and "subDocumentData"
            // to match frontend behavior. subDocumentData can contain nested data that Firestore
            // doesn't support (causing "invalid nested entity" errors), and selectedObjectUids
            // is only meaningful to the current user session.
            forward: latestPatch.forward.filter(patchFilter),
            reverse: latestPatch.reverse.filter(patchFilter),
            previousUid: latestPatch.previousUid,
        },
        actionPreviousUid: previousDocument.latestActionRecordUid ?? null,
        actionDocumentUid: newDocument.uid,
        actionTimestamp: now,
        actionUpdatesDocumentVersion: true,
        actionUserUid: actionUserUid ?? newDocument.owner_uid,
        actionName: typeof actionName === "string" ? actionName : actionName(),
        actionRecordUid: latestPatch.uid,
    })
        .then(() => {
        logger.info(`Saved action record for document ${newDocument.uid}, new action record uid ${latestPatch.uid}`);
    });
    // Update document
    const updateDocumentPromise = documentRepository.save(newDocument).then(() => {
        logger.info(`Saved document ${newDocument.uid} by action ${actionName}`);
    });
    await Promise.all([updateDocumentPromise, updateActionRecordPromise]);
    return newDocument;
}
export async function updateAndPersistWithResult(previousDocument, editFn, actionName, dependencies, actionUserUid) {
    let result;
    const mutator = (draft) => {
        result = editFn(draft);
    };
    const resolvedActionName = typeof actionName === "string" ? actionName : () => actionName(result);
    const document = await updateAndPersistDocumentData(previousDocument, mutator, resolvedActionName, dependencies, actionUserUid);
    return { document, result, changed: document !== previousDocument };
}
//# sourceMappingURL=storage.js.map